<template>
  <!--
    1. Removed absolute-full from q-page so it respects the toolbar padding.
    2. Added standard flex column classes.
  -->
  <q-page class="bg-grey-1 column no-wrap">
    <!-- TOP UI BLOCK (col-auto forces it to only take up the vertical space it needs) -->
    <div class="col-auto q-px-sm q-pt-sm q-pb-none">
      <!-- Role Toggle (Home vs Visitor) -->
      <q-btn-toggle
        v-model="selectedRole"
        spread
        dense
        no-caps
        unelevated
        toggle-color="primary"
        color="white"
        text-color="primary"
        class="border-primary q-mb-sm"
        style="border: 1px solid var(--q-primary)"
        :options="[
          { label: 'Home', value: 'home' },
          { label: 'Visitor', value: 'visiting' },
        ]"
      />
      <q-dialog
        v-model="isEditing"
        :position="$q.screen.lt.sm ? 'top' : 'standard'"
        :maximized="$q.screen.lt.sm"
        transition-show="slide-down"
        transition-hide="slide-up"
      >
        <q-card class="column full-height" style="max-height: 90vh; width: 100%; max-width: 500px">
          <q-toolbar class="bg-primary text-white col-auto">
            <q-toolbar-title>Edit Batter</q-toolbar-title>
            <q-btn flat round dense icon="close" v-close-popup />
          </q-toolbar>

          <q-card-section class="col scroll q-pa-md">
            <lineup-entry-edit
              v-model="showBatterEditor"
              :role="selectedRole"
              @saved="onBatterSaved"
            />
          </q-card-section>
        </q-card>
      </q-dialog>

      <!-- IF A TEAM IS ASSIGNED TO THIS ROLE: Show Header & Options -->
      <div v-if="gameStore.activeTeam" class="row items-center justify-between q-mb-sm">
        <div class="text-subtitle1 text-weight-bold text-primary">
          {{ gameStore.activeTeam.name }}
          <span class="text-caption text-grey-7 text-weight-regular">
            ({{ selectedRole === 'home' ? 'Home' : 'Visitor' }})
          </span>
        </div>
        <q-btn
          @click="handleSaveLineUptoTeam"
          color="info"
          flat
          size="md"
          label="Save Default"
          icon="save"
        />
      </div>

      <!-- IF NO TEAM IS ASSIGNED TO THIS ROLE: Show Selection Dropdown -->
      <div v-else class="q-mb-sm">
        <q-select
          v-model="selectedTeam"
          :options="teams"
          option-label="name"
          label="Select Team"
          outlined
          dense
          class="bg-white"
          @update:model-value="confirmSetup"
        >
          <template v-slot:prepend>
            <q-icon name="group" />
          </template>
        </q-select>
      </div>

      <!-- VIEW MODE TOGGLE (BATTING VS DEFENSE) -->
      <div class="row justify-center q-mb-sm" v-if="gameStore.activeTeam">
        <q-btn-toggle
          v-model="viewMode"
          rounded
          unelevated
          no-caps
          size="md"
          toggle-color="secondary"
          color="white"
          text-color="grey-8"
          class="shadow-1"
          :options="[
            { label: 'Batting', value: 'batting', icon: 'format_list_numbered' },
            { label: 'Build', value: 'build', icon: 'playlist_add_check' },
            { label: 'Defense', value: 'defense', icon: 'security' },
          ]"
        />
      </div>
    </div>
    <!-- END TOP UI BLOCK -->

    <!-- BOTTOM GRIDS BLOCK -->
    <!-- The col relative-position combo forces this wrapper to consume exactly the remaining height -->
    <div class="col relative-position" v-if="gameStore.activeTeam">
      <!-- absolute-full maps exactly to the parent bounds. 'scroll' ensures internal scrolling. -->
      <div class="absolute-full scroll q-pa-md row q-col-gutter-md justify-center">
        <!-- BUILD ORDER VIEW -->
        <div class="col-12 col-md-8 col-lg-6 full-height" v-show="viewMode === 'build'">
          <q-card bordered flat class="bg-white rounded-borders column full-height">
            <q-card-section class="shrink q-py-sm bg-grey-2 row items-center justify-between">
              <div class="text-caption text-weight-bold text-uppercase">Tap Each Slot in Order</div>
              <q-btn flat dense label="Reset" color="grey-7" @click="resetBuildOrder" />
            </q-card-section>

            <!-- Inner scrollable list -->
            <q-card-section class="col scroll q-pa-none">
              <q-list separator>
                <q-item
                  v-for="slot in buildSlots"
                  :key="'build-slot-' + slot.order"
                  clickable
                  :active="activeBuildSlot === slot.order"
                  active-class="bg-blue-1"
                  @click="activeBuildSlot = slot.order"
                >
                  <q-item-section avatar>
                    <div class="text-weight-bold text-primary">{{ slot.order }}</div>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label v-if="slot.player">
                      #{{ slot.player.uniformNumber || '-' }} {{ slot.player.name }}
                    </q-item-label>
                    <q-item-label v-else class="text-grey-5">Tap to assign</q-item-label>
                  </q-item-section>
                  <q-item-section side v-if="slot.player">
                    <q-icon name="check_circle" color="positive" />
                  </q-item-section>
                </q-item>
              </q-list>
            </q-card-section>

            <!-- Picker sheet -->
            <q-dialog v-model="pickerOpen" position="bottom">
              <q-card
                class="column no-wrap"
                style="
                  width: 100%;
                  border-top-left-radius: 16px;
                  border-top-right-radius: 16px;
                  max-height: 70vh;
                "
              >
                <q-card-section class="shrink bg-grey-2" style="border-bottom: 1px solid #e0e0e0">
                  <div class="text-subtitle1 text-weight-bold">
                    Who bats {{ activeBuildSlot }}{{ ordinalSuffix(activeBuildSlot) }}?
                  </div>
                </q-card-section>

                <q-card-section class="col-grow scroll q-pa-md">
                  <div class="row q-gutter-sm">
                    <q-btn
                      v-for="p in unassignedPlayers"
                      :key="p.player.id"
                      outline
                      color="primary"
                      class="col-5 text-weight-bold"
                      @click="assignBuildSlot(p)"
                    >
                      #{{ p.player.uniformNumber || '-' }} {{ p.player.name }}
                    </q-btn>
                  </div>
                  <div
                    v-if="unassignedPlayers.length === 0"
                    class="text-center text-grey-6 q-pa-md"
                  >
                    All players assigned.
                  </div>
                </q-card-section>
              </q-card>
            </q-dialog>
          </q-card>
        </div>

        <!-- BATTING ORDER VIEW -->
        <div
          class="col-12 col-md-8 col-lg-6 column no-wrap full-height"
          v-show="viewMode === 'batting'"
        >
          <div class="col-auto">
            <lineup-entry
              v-show="!isEditing"
              v-model="showBatterEditorAdd"
              :role="selectedRole"
              @saved="onBatterSaved"
            />
          </div>
          <div class="col scroll q-mt-sm">
            <lineup-list :role="selectedRole" v-model="showBatterEditor" />
          </div>
        </div>

        <!-- DEFENSIVE ALIGNMENT VIEW -->
        <div class="col-12 col-md-8 col-lg-6 full-height" v-show="viewMode === 'defense'">
          <q-card bordered flat class="bg-white rounded-borders column full-height no-wrap">
            <q-card-section
              class="shrink q-py-sm row justify-between items-center"
              :class="isGameLive ? 'bg-warning text-black' : 'bg-grey-2'"
              style="border-bottom: 1px solid #e0e0e0; z-index: 2"
            >
              <div class="text-subtitle2 text-weight-bold text-uppercase">
                {{ isGameLive ? 'Live Defensive Alignment' : 'Starting Defensive Alignment' }}
              </div>
              <q-badge :color="isGameLive ? 'black' : 'primary'"
                >{{ activeLineup?.length || 0 }} Players</q-badge
              >
            </q-card-section>

            <!-- Scrollable List Area -->
            <q-card-section class="col scroll q-pa-sm">
              <div
                class="row q-col-gutter-xs text-weight-bold text-caption text-grey-8 q-mb-sm text-center"
                style="border-bottom: 1px solid #eee; padding-bottom: 4px"
              >
                <div class="col-2">Ord</div>
                <div class="col-3">Num #</div>
                <div class="col-3 text-left">Player</div>
                <div class="col-4">Position</div>
              </div>

              <div
                v-for="(batter, idx) in activeLineup"
                :key="'def-grid-slot-' + idx"
                class="row q-col-gutter-xs q-mb-xs items-center q-py-xs rounded-borders"
                :class="{ 'bg-green-1': isPlayerChanged(batter.player.id) }"
              >
                <div class="col-2 text-center text-weight-bold text-primary">
                  {{ batter.battingOrder }}
                </div>

                <div class="col-3">
                  <q-input
                    v-model.number="batter.player.uniformNumber"
                    type="number"
                    dense
                    outlined
                    placeholder="#"
                    @update:model-value="gameStore.saveActiveGame()"
                  />
                </div>

                <div class="col-3 text-left text-body2 text-truncate">
                  <span class="text-weight-medium">{{ batter.player.name }}</span>
                </div>

                <div class="col-4">
                  <q-select
                    :model-value="getDraftPosition(batter.player.id)"
                    :options="shortPositionOptions"
                    emit-value
                    map-options
                    dense
                    outlined
                    options-dense
                    @update:model-value="onPositionSelect(batter, $event)"
                  />
                </div>
              </div>

              <div
                v-if="!activeLineup || activeLineup.length === 0"
                class="text-center text-grey-6 q-pa-xl"
              >
                <q-icon name="group" size="2rem" class="q-mb-sm opacity-50" />
                <div>No players in lineup. Add batters first!</div>
              </div>
            </q-card-section>

            <!-- Sticky Footer -->
            <q-card-actions
              v-if="hasDefenseChanges"
              align="between"
              class="shrink bg-grey-1 q-pa-md"
              style="border-top: 1px solid #e0e0e0; z-index: 2"
            >
              <q-btn flat label="Discard" color="negative" @click="initDraftDefense" />
              <q-btn
                unelevated
                label="Save Defense"
                color="primary"
                icon-right="check"
                @click="commitDefenseChanges"
              />
            </q-card-actions>
          </q-card>
        </div>
      </div>
    </div>
    <!-- END BOTTOM GRIDS BLOCK -->

    <!-- Empty State Hint -->
    <div v-else class="col flex flex-center">
      <div class="text-center text-grey-7">
        <q-icon name="info" size="xl" class="q-mb-sm" />
        <div class="text-h6">Please select a team to manage their lineup.</div>
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import LineupEntry from 'components/LineupEntryEdit.vue'
import LineupList from 'components/LineupList.vue'
import LineupEntryEdit from 'components/LineupEntryEdit.vue'

import { useNotify } from 'src/helpers/notify'
import { useGameStore } from 'stores/game-store'
import { useAsyncGuard } from 'src/composables/useAsyncGuard'
import { db } from 'src/models/db'
import type {
  Team,
  LineupEntry as LineupEntryType,
  DefenseSnapshot,
  Position,
} from 'src/stores/base-types'

const gameStore = useGameStore()

onMounted(async () => {
  await loadTeams()
})

const teams = ref<Team[]>([])
const selectedRole = ref<'home' | 'visiting'>('home')
const selectedTeam = ref<Team | null>(null)
const showBatterEditor = ref(false)
const showBatterEditorAdd = ref(false)
const showBatterMenu = ref(false)
const viewMode = ref<'batting' | 'defense' | 'build'>('batting')

// -- DRAFT STATE FOR DEFENSE --
const draftDefense = ref<DefenseSnapshot>({})
const originalDefense = ref<DefenseSnapshot>({})

const isEditing = computed({
  get() {
    return gameStore.editingLineupEntry !== null
  },
  set(value) {
    showBatterMenu.value = value
  },
})

const activeLineup = computed(() => {
  return selectedRole.value === 'home' ? gameStore.homeLineup : gameStore.visitingLineup
})

const isGameLive = computed(() => {
  return gameStore.activeGameSummary && gameStore.plateAppearances.length > 0
})

const shortPositionOptions = [
  { label: '1 (P)', value: 1 },
  { label: '2 (C)', value: 2 },
  { label: '3 (1B)', value: 3 },
  { label: '4 (2B)', value: 4 },
  { label: '5 (3B)', value: 5 },
  { label: '6 (SS)', value: 6 },
  { label: '7 (LF)', value: 7 },
  { label: '8 (CF)', value: 8 },
  { label: '9 (RF)', value: 9 },
  { label: '10 (DH)', value: 10 },
  { label: '11 (PH)', value: 11 },
  { label: '12 (PR)', value: 12 },
  { label: '13 (EH)', value: 13 },
]

async function loadTeams() {
  try {
    teams.value = await db.teams.toArray()
  } catch (error) {
    console.error('Failed to load teams:', error)
    useNotify('Failed to load teams.', 2000, 'negative')
  }
}

watch(
  selectedRole,
  (newRole) => {
    const teamForRole = newRole === 'home' ? gameStore.teamHome : gameStore.teamVisiting

    if (teamForRole) {
      gameStore.setActiveTeam(teamForRole, newRole)
    } else {
      gameStore.activeTeam = null
    }

    selectedTeam.value = teamForRole || null
    gameStore.clearEditingBatter()
  },
  { immediate: true },
)

// Initialize draft anytime we enter defense mode or change teams
watch(
  () => [viewMode.value, selectedRole.value, isGameLive.value],
  ([newViewMode]) => {
    if (newViewMode === 'defense') {
      initDraftDefense()
    }
  },
  { immediate: true },
)

function confirmSetup(team: Team) {
  if (team) {
    gameStore.setActiveTeam(team, selectedRole.value)
  }
}

async function handleSaveLineUptoTeam() {
  useNotify('Lineup saved to Team!', 1000, 'positive')
  await gameStore.populateTeamFromLineup()
}

function onBatterSaved() {
  useNotify('Lineup updated successfully!', 1000, 'positive')
}

// --- DEFENSE DRAFT & COMMIT LOGIC ---

function initDraftDefense() {
  const snap: DefenseSnapshot = {}

  if (isGameLive.value && gameStore.viewedPlateAppearance) {
    // If live, clone the active Plate Appearance's snapshot
    const liveSnapshot = gameStore.viewedPlateAppearance.defenseSnapshot || {}
    Object.assign(snap, liveSnapshot)
  } else {
    // If pre-game, map startingPositions
    activeLineup.value?.forEach((b) => {
      if (b.startingPosition >= 1 && b.startingPosition <= 9 && b.player.id) {
        snap[b.startingPosition as Position] = {
          ...snap[b.startingPosition as Position],
          ...{ playerId: b.player.id },
        }
      }
    })
  }

  originalDefense.value = { ...snap }
  draftDefense.value = { ...snap }
}

const hasDefenseChanges = computed(() => {
  return JSON.stringify(draftDefense.value) !== JSON.stringify(originalDefense.value)
})

const getDraftPosition = (playerId: number | undefined): number => {
  if (!playerId) return 10
  for (const [pos, id] of Object.entries(draftDefense.value)) {
    if (id.playerId === playerId) return Number(pos)
  }

  // If not in 1-9 draft, return their startingPosition (e.g. DH/EH/Bench) as fallback
  const batter = activeLineup.value?.find((b) => b.player.id === playerId)
  return batter?.startingPosition ?? 10
}

const isPlayerChanged = (playerId: number | undefined): boolean => {
  if (!playerId) return false
  let origPos = 10
  for (const [pos, id] of Object.entries(originalDefense.value)) {
    if (id.playerId === playerId) origPos = Number(pos)
  }
  return origPos !== getDraftPosition(playerId)
}

function onPositionSelect(batter: LineupEntryType, newPos: number) {
  const playerId = batter.player.id
  if (!playerId) return

  // Scrub the player from any existing draft position
  for (const [posStr, id] of Object.entries(draftDefense.value)) {
    if (id.playerId === playerId) {
      delete draftDefense.value[Number(posStr) as Position]
    }
  }

  // If assigning them to a field position (1-9), assign them, displacing whoever was there
  if (newPos >= 1 && newPos <= 9) {
    draftDefense.value[newPos as Position] = {
      ...draftDefense.value[newPos as Position],
      playerId: playerId,
    }
  }

  // Pre-game Only: Immediately sync non-field positions (DH, EH) into their entry
  if (!isGameLive.value && newPos >= 10) {
    batter.startingPosition = newPos
  }
}

const buildDefensiveSwapSummary = (
  oldSnap: DefenseSnapshot,
  newSnap: DefenseSnapshot,
  allPlayers: LineupEntryType[],
): string => {
  const changes: string[] = []
  const posNames: Record<number, string> = {
    1: 'P',
    2: 'C',
    3: '1B',
    4: '2B',
    5: '3B',
    6: 'SS',
    7: 'LF',
    8: 'CF',
    9: 'RF',
  }

  for (let pos = 1; pos <= 9; pos++) {
    const oldPlayer = oldSnap[pos as Position]?.playerId
    const newPlayer = newSnap[pos as Position]?.playerId

    if (newPlayer && newPlayer !== oldPlayer) {
      const playerEntry = allPlayers.find((p) => p.player.id === newPlayer)
      const name = playerEntry ? `#${playerEntry.player.uniformNumber || '-'}` : 'Unknown'
      changes.push(`${name} to ${posNames[pos]}`)
    }
  }
  return changes.length > 0
    ? `Defensive shifts: ${changes.join(', ')}.`
    : 'Defensive alignments updated.'
}

async function commitDefenseChanges() {
  if (isGameLive.value) {
    // 1. Live Game: Batch sub via the Game Store action
    const lineup = activeLineup.value || []
    const allPlayers = lineup.concat(lineup.flatMap((l) => l.substitutions || []))
    const summaryNotes = buildDefensiveSwapSummary(
      originalDefense.value,
      draftDefense.value,
      allPlayers,
    )

    await gameStore.applyDefensiveChanges(draftDefense.value, summaryNotes)
  } else {
    // 2. Pre-Game: Sync the draft back to startingPositions
    activeLineup.value?.forEach((b) => {
      if (b.player.id) {
        const draftPos = getDraftPosition(b.player.id)
        // Only update if it's a field position (1-9) or if they were on the field and got benched
        if (draftPos >= 1 && draftPos <= 9) {
          b.startingPosition = draftPos
        } else if (b.startingPosition >= 1 && b.startingPosition <= 9) {
          b.startingPosition = 10 // Bench them
        }
      }
    })
    await gameStore.saveActiveGame()
  }

  initDraftDefense() // Reset original bounds to match
  useNotify('Defense saved!', 1000, 'positive')
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { isPending: isReversing, guard: guardReverse } = useAsyncGuard()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleReverseOrder = guardReverse(async () => {
  gameStore.reverseLineup(selectedRole.value)
  await gameStore.saveActiveGame()
  useNotify('Batting order reversed', 1000, 'positive')
})

// --- BUILD ORDER STATE ---
const activeBuildSlot = ref<number | null>(null)
const pickerOpen = computed({
  get: () => activeBuildSlot.value !== null,
  set: (v: boolean) => {
    if (!v) activeBuildSlot.value = null
  },
})

const buildSlots = computed(() => {
  const lineup = activeLineup.value || []
  return lineup
    .slice()
    .sort((a, b) => a.battingOrder - b.battingOrder)
    .map((entry) => ({ order: entry.battingOrder, player: entry.player }))
})

const unassignedPlayers = computed(() => {
  const lineup = activeLineup.value || []
  const currentSlotPlayerId = buildSlots.value.find((s) => s.order === activeBuildSlot.value)
    ?.player?.id
  return lineup.filter(
    (e): e is LineupEntryType & { player: { id: number } } =>
      e.player.id !== undefined && e.player.id !== currentSlotPlayerId,
  )
})

function ordinalSuffix(n: number | null): string {
  if (n == null) return ''
  const j = n % 10,
    k = n % 100
  if (j === 1 && k !== 11) return 'st'
  if (j === 2 && k !== 12) return 'nd'
  if (j === 3 && k !== 13) return 'rd'
  return 'th'
}

function assignBuildSlot(picked: LineupEntryType) {
  if (activeBuildSlot.value === null) return
  const lineup = activeLineup.value
  if (!lineup) return

  const targetSlot = activeBuildSlot.value
  const pickedEntry = lineup.find((e) => e.player.id === picked.player.id)
  const currentAtTarget = lineup.find((e) => e.battingOrder === targetSlot)

  if (pickedEntry && currentAtTarget && pickedEntry.player.id !== currentAtTarget.player.id) {
    // Swap: the picked player takes this slot; whoever was here takes the picked player's old slot
    const pickedOldOrder = pickedEntry.battingOrder
    pickedEntry.battingOrder = targetSlot
    currentAtTarget.battingOrder = pickedOldOrder
  }

  void gameStore.saveActiveGame()

  // Auto-advance to next unfilled-feeling slot
  const nextOrder = targetSlot < lineup.length ? targetSlot + 1 : null
  activeBuildSlot.value = nextOrder
}

function resetBuildOrder() {
  const lineup = activeLineup.value
  if (!lineup) return
  lineup
    .slice()
    .sort((a, b) => (a.player.name > b.player.name ? 1 : -1))
    .forEach((entry, i) => (entry.battingOrder = i + 1))
  void gameStore.saveActiveGame()
}
</script>
